import * as React from 'react';
import {useDispatch} from 'react-redux';

import {analyticsClient} from '../../analytics-client';

import {
    useGetBaseContext,
    useGetTeamAiIdentity,
    useTrackAiAssistantConversation,
} from './graphql/hooks';
import {startConversationTracker, initializeChatbotWithBaseContext} from './helpers';
import type {AgentKey, Chatbot} from './helpers';
import {setChatbot as setChatbotAction} from './redux/agent-reducer';

// https://docs.teamai.com/sdks/embed-sdk/overview#initialize-embed-sdk
const TEAM_AI_CHATBOT_READY_EVENT = 'teamAIChatbotReady';
const TEAM_AI_EMBED_SRC = 'https://app.teamai.com/chatbot-embed.js';
const TEAM_AI_EMBED_ID = 'teamai-chatbot-embed';

export function useInitializeChatbot(agentKey: AgentKey) {
    const [chatbotIsReady, setChatbotIsReady] = React.useState<boolean>(false);
    const [chatbot, setChatbot] = React.useState<Chatbot | null | undefined>(null);

    // We have a microtask (startUsageReporter) running in the background that's
    // reporting usage to the server. We use this ref to tell it when to stop.
    const keepReportingUsage = React.useRef<boolean>(false);

    const {userIdentity} = useGetTeamAiIdentity();
    const {baseContext} = useGetBaseContext();
    const {trackAiAssistantConversation} = useTrackAiAssistantConversation();
    const dispatch = useDispatch();

    // There's gotta be a way to initialize a new chatbot dynamically, but for now
    // we'll just load the script each time we want to load a new agent.
    React.useEffect(() => {
        function handleReady() {
            setChatbotIsReady(true);
        }

        window.addEventListener(TEAM_AI_CHATBOT_READY_EVENT, handleReady);

        const script = document.createElement('script');

        const existingScript = document.getElementById(TEAM_AI_EMBED_ID);
        if (document.body && existingScript) {
            document.body.removeChild(existingScript);
        }

        script.id = TEAM_AI_EMBED_ID;
        script.src = TEAM_AI_EMBED_SRC;
        script.async = true;

        if (document.body) {
            document.body.appendChild(script);
        }

        return () => {
            window.removeEventListener(TEAM_AI_CHATBOT_READY_EVENT, handleReady);

            if (document.body) {
                document.body.removeChild(script);
            }
        };
    }, []);

    React.useEffect(() => {
        if (chatbotIsReady && userIdentity && baseContext && !chatbot) {
            const initializedChatbot = initializeChatbotWithBaseContext(
                agentKey,
                userIdentity,
                baseContext
            );
            setChatbot(initializedChatbot);

            if (initializedChatbot) {
                keepReportingUsage.current = true;
                startConversationTracker(
                    initializedChatbot,
                    keepReportingUsage,
                    trackAiAssistantConversation
                );
            }

            dispatch(setChatbotAction(initializedChatbot));

            analyticsClient.track(analyticsClient.EVENTS.TRACK_AI_ASSISTANT_INITIATED, {
                type: agentKey,
            });
        }

        return () => {
            keepReportingUsage.current = false;

            dispatch(setChatbotAction(null));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatbotIsReady, userIdentity, baseContext]);

    return chatbot;
}
